import React, { useId, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { candidacyService } from '@/shared/services/candidacyService';
import { PREVIEW_LOCK_NOTE_ID } from '@/shared/hooks/usePreviewLock';
import useUnloadWarning from '@/shared/hooks/useUnloadWarning';
import { readDraft, writeDraft } from '@/shared/utils/drafts';
import { Paragraphs } from './CandidateAnswers';
import {
    SAVE_FALLBACK,
    WriteStatus,
    formatDate,
    refusalOf,
    reviewerStageOf,
} from './reviewerStages';

const MAX_LENGTH = 2000;

const draftKeyOf = (candidacyId) => `note.${candidacyId}`;

/** Whether this tab holds a note on the candidacy that was not added yet. */
export const hasNoteDraft = (candidacyId) =>
    Boolean(readDraft(draftKeyOf(candidacyId)));

// Faculty read who wrote an entry as "You", "From faculty" or "From the
// program", never a name; coordinators read the author's name.
function authorOf(entry, mode) {
    if (mode === 'coordinator') {
        return entry.authorName || 'A reviewer';
    }
    if (entry.mine) return 'You';
    return entry.role === 'faculty' ? 'From faculty' : 'From the program';
}

function visibilityOf(entry) {
    return entry.visibility === 'shared'
        ? 'Shared with the student'
        : 'Reviewers only';
}

/**
 * The notes on one candidacy, and one composer to add to them. A note is for
 * the reviewers unless the reviewer chooses to share it with the student.
 * The server sends faculty only the entries they may read.
 * Text typed but not added is kept as a draft for this candidacy until it is
 * added or discarded, so leaving the student (Back, another student, another
 * section) and coming back finds it again.
 */
export default function CandidateFeedback({
    candidacy,
    mode,
    disabled,
    onChange,
    onReload,
}) {
    const id = useId();
    const draftKey = draftKeyOf(candidacy.id);
    const [text, setTextState] = useState(() => readDraft(draftKey) || '');
    const [restored] = useState(() => text.trim() !== '');
    const [visibility, setVisibility] = useState('internal');
    const [busy, setBusy] = useState(false);
    const [refusal, setRefusal] = useState(null);
    const [notice, setNotice] = useState('');
    const entries = candidacy.feedback || [];
    const readers =
        mode === 'coordinator'
            ? 'Only reviewers read a note unless you share it.'
            : "Only you and the program's coordinators read a note unless you share it.";
    const empty = text.trim() === '';
    const tooLong = text.length > MAX_LENGTH;
    const reason = empty
        ? 'Write the note to add it.'
        : tooLong
          ? `A note can be at most ${MAX_LENGTH.toLocaleString('en-US')} characters.`
          : '';
    useUnloadWarning(!empty);

    const setText = (value) => {
        setTextState(value);
        writeDraft(draftKey, value.trim() ? value : null);
    };

    const submit = async (event) => {
        event.preventDefault();
        if (disabled || busy || reason) return;
        setBusy(true);
        setRefusal(null);
        setNotice('');
        try {
            const updated = await candidacyService.addFeedback(
                candidacy.id,
                text,
                visibility
            );
            setText('');
            setVisibility('internal');
            setNotice('Note added.');
            onChange?.(updated, 'Note added.');
        } catch (err) {
            setRefusal(refusalOf(err, SAVE_FALLBACK));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            {entries.length === 0 ? (
                <p className="mb-3">No notes yet.</p>
            ) : (
                <ul className="list-unstyled mb-3">
                    {entries.map((entry, index) => (
                        <li key={`${entry.at}-${index}`} className="mb-2">
                            <p className="mb-0 small text-body-secondary">
                                {authorOf(entry, mode)}
                                {entry.stage &&
                                    `, at ${reviewerStageOf(entry.stage).label}`}
                                {entry.at && `, ${formatDate(entry.at)}`}
                                {`. ${visibilityOf(entry)}.`}
                            </p>
                            <Paragraphs value={entry.text} />
                        </li>
                    ))}
                </ul>
            )}
            <Form onSubmit={submit} noValidate>
                <Form.Group controlId={`${id}-text`} className="mb-2">
                    <Form.Label>Add a note</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        maxLength={MAX_LENGTH}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        aria-describedby={`${id}-readers ${id}-count ${id}-reason${restored && !empty ? ` ${id}-kept` : ''}`}
                    />
                    {restored && !empty && (
                        <Form.Text id={`${id}-kept`} as="div" muted>
                            Your note from earlier was kept. Add it, or discard
                            it.
                        </Form.Text>
                    )}
                    <Form.Text id={`${id}-readers`} as="div" muted>
                        {readers}
                    </Form.Text>
                    <Form.Text id={`${id}-count`} as="div" muted>
                        {`${text.length.toLocaleString('en-US')} of ${MAX_LENGTH.toLocaleString('en-US')} characters`}
                    </Form.Text>
                </Form.Group>
                <Form.Check
                    type="checkbox"
                    id={`${id}-shared`}
                    className="mb-2"
                    label="Share with the student"
                    checked={visibility === 'shared'}
                    onChange={(e) =>
                        setVisibility(e.target.checked ? 'shared' : 'internal')
                    }
                />
                <Button
                    type="submit"
                    variant="outline-primary"
                    className="hiring-note-action"
                    disabled={disabled || busy || Boolean(reason)}
                    aria-describedby={
                        [
                            reason ? `${id}-reason` : null,
                            disabled ? PREVIEW_LOCK_NOTE_ID : null,
                        ]
                            .filter(Boolean)
                            .join(' ') || undefined
                    }
                >
                    Add note
                </Button>
                {!empty && (
                    <Button
                        type="button"
                        variant="link"
                        className="ms-1 hiring-note-action"
                        disabled={busy}
                        onClick={() => {
                            setText('');
                            setVisibility('internal');
                        }}
                    >
                        Discard note
                    </Button>
                )}
                <span
                    id={`${id}-reason`}
                    className="ms-2 small text-body-secondary"
                >
                    {reason}
                </span>
            </Form>
            <WriteStatus
                refusal={refusal}
                notice={notice}
                onReload={onReload}
            />
        </div>
    );
}
